import { app } from '../../../app';
import { groupFromDto } from '../user-directories/group.dto';

class ApplicationsResource {

    /** @ngInject */
    constructor($http) {
        this.$http = $http;
    }

    getAllApplicationsForCompany(companyId) {
        const url = `app/rest/v2/company/${companyId}/applications`;
        return this.$http.get(url).then(({data}) => data);
    }

    getAllComputerProtectionApplicationsForCompany(companyId) {
        const url = `app/rest/v2/company/${companyId}/applications`;
        const params = {type: 'computer'};
        return this.$http.get(url, {params}).then(({data}) => data);
    }

    getAllCustomApplicationsForCompany(companyId) {
        const url = `app/rest/v2/company/${companyId}/applications`;
        const params = {type: 'custom'};
        return this.$http.get(url, {params}).then(({data}) => data);
    }

    getApplicationConfiguration(companyId, applicationId) {
        const url = `app/rest/v2/company/${companyId}/applications/${applicationId}`;

        return this.$http.get(url)
            .then(({data}) => ({
                ...data,
                loginRoles: data.loginRoles || [],
            }))
            .then((data) => ({
                ...data,
                ilQrCodeSettings: data.ilQrCodeSettings && {
                    qrCodeBackgroundColor: `#${data.ilQrCodeSettings.qrCodeBackgroundColor}`,
                    qrCodeForegroundColor: `#${data.ilQrCodeSettings.qrCodeForegroundColor}`,
                    qrCodeSize: data.ilQrCodeSettings.qrCodeSize,
                },
                irQrCodeSettings: data.irQrCodeSettings && {
                    qrCodeBackgroundColor: `#${data.irQrCodeSettings.qrCodeBackgroundColor}`,
                    qrCodeForegroundColor: `#${data.irQrCodeSettings.qrCodeForegroundColor}`,
                    qrCodeSize: data.irQrCodeSettings.qrCodeSize,
                },
            }));
    }

    updateApplicationConfiguration(companyId, applicationId, config) {
        const url = `app/rest/v2/company/${companyId}/applications/${applicationId}`;
        return this.$http.put(url, sanitize(config)).then(({data}) => data);

        function sanitize(config) {
            return {
                ...config,
                companyAdDomains: undefined,
                ssoPostUrl: config.ssoEnabled && config.ssoPostUrl || undefined,
            }
        }
    }

    getApplicationHelpInfo(companyId, applicationId) {
        const url = `app/rest/v2/company/${companyId}/applications/${applicationId}/helpinfo`;
        return this.$http.get(url).then(({data}) => data);
    }

    getApplicationSamlAssertionAttributes(companyId, applicationId) {
        const url = `app/rest/v2/company/${companyId}/applications/${applicationId}/assertions`;
        return this.$http.get(url).then(({data}) => data);
    }

    updateApplicationSamlAssertionAttributes(companyId, applicationId, attribs) {
        const url = `app/rest/v2/company/${companyId}/applications/${applicationId}/assertions`;
        return this.$http.put(url, attribs).then(({data}) => data);
    }

    deleteApplication(companyId, applicationId) {
        const url = `app/rest/v2/company/${companyId}/applications/${applicationId}`;
        const params = {action: 'delete'};
        return this.$http.delete(url, null, {params}).then(({data}) => data);
    }

    changeApplicationStatus(companyId, applicationId, status) {
        const url = `app/rest/v2/company/${companyId}/applications/${applicationId}`;
        const params = {action: 'status', status: status};
        return this.$http.put(url, null, {params}).then(({data}) => data);
    }

    getApplicationAssignedGroups(companyId, applicationId) {
        const url = `app/rest/v2/company/${companyId}/applications/${applicationId}/assignedgroups`;
        return this.$http.get(url).then(({data}) => data)
            .then((groups) => groups.map((group) => {
                const appRoleIds = group.appRoleIds || [];
                return {...group, appRoleIds};
            }))
            .then((groups) => groups.map(groupFromDto))
    }

    getApplicationNotAssignedGroups(companyId, applicationId) {
        const url = `app/rest/v2/company/${companyId}/applications/${applicationId}/notassignedgroups`;
        return this.$http.get(url).then(({data}) => data)
            .then((groups) => groups.map(groupFromDto))
    }

    assignGroups(companyId, applicationId, groupsWithRoles) {
        const url = `app/rest/v2/company/${companyId}/applications/${applicationId}/groups`;
        const params = {action: 'assign'};
        return this.$http.put(url, groupsWithRoles, {params}).then(({data}) => data);
    }

    unassignGroup(companyId, applicationId, groupId) {
        const url = `app/rest/v2/company/${companyId}/applications/${applicationId}/group`;
        const params = {action: 'unassign'};
        return this.$http.put(url, groupId, {params}).then(({data}) => data);
    }

    updateAwsGroupRoles(companyId, applicationId, groupsWithRoles) {
        const url = `app/rest/v2/company/${companyId}/applications/${applicationId}/groups`;
        const params = {action: 'updateroles'};
        return this.$http.put(url, groupsWithRoles, {params}).then(({data}) => data);
    }

    getUsageData(companyId, applicationId, filterUser, periodFilter, displayData, saaspassId, startDate, endDate) {
        const url = `app/rest/v2/company/${companyId}/applications/${applicationId}/usagedata`;
        const params = {
            filterUser,
            periodFilter,
            displayData,
            saaspassId,
            startDate,
            endDate,
            timeZone: new Date().getTimezoneOffset() / -60,
        };
        return this.$http.get(url, {params}).then(({data}) => data);
    }

    regeneratePassword(companyId, applicationId) {
        const url = `app/rest/v2/company/${companyId}/applications/${applicationId}/regeneratepass`;
        return this.$http.put(url).then(({data}) => data);
    }

    sha2CertUpdate(companyId, applicationId) {
        const url = `app/rest/v2/company/${companyId}/applications/${applicationId}/sha2CertUpdate`;
        return this.$http.get(url).then(({data}) => data);
    }

    changeNonDomainComputerProtectionKey(companyId, applicationId) {
        const url = `app/rest/v2/company/${companyId}/applications/${applicationId}/changeNonDomainKey`;
        return this.$http.post(url).then(({data}) => data);
    }

    getApplicationApiCredentials(companyId, applicationId, params) {
        const url = `app/rest/v2/company/${companyId}/applications/${applicationId}/credentials`;
        return this.$http.get(url, {params}).then(({data}) => data);
    }

    uploadCustomIcon(companyId, applicationId, file) {
        const fd = new FormData();
        fd.append('file', file);
        const url = `app/rest/v2/company/${companyId}/applications/${applicationId}/photo`;
        return this.$http.post(url, fd,{
            transformRequest: angular.identity,
            headers: {
                'Content-Type': undefined,
            },
        }).then(({data}) => data);
    }
}

app.service('ApplicationsResource', ApplicationsResource);



// WEBPACK FOOTER //
// ./src/portal/states/company/manage-application/applications-resource.service.js